import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { ComposedChart, Bar, Line, LineChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, formatTL, MONTH_NAMES } from "../lib/api";

function DeltaBadge({ current, previous, invert = false, testId }) {
  if (!previous) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const up = pct >= 0;
  const good = invert ? !up : up;
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-0.5 text-xs font-semibold rounded-full px-2 py-0.5 ${
        good ? "text-emerald-400 bg-emerald-500/15" : "text-red-400 bg-red-500/15"
      }`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      %{Math.abs(pct).toFixed(0)} geçen yıla göre
    </span>
  );
}

export default function YearlyReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      api.get(`/reports/yearly?year=${year}`),
      api.get(`/reports/yearly?year=${year - 1}`),
    ]);
    setCurrent(c.data);
    setPrevious(p.data);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (!current) return null;

  const chartData = current.months.map((m, i) => ({
    name: MONTH_NAMES[i].slice(0, 3),
    income: m.income,
    expense: m.expense,
    net: m.net,
  }));
  const compareData = current.months.map((m, i) => ({
    name: MONTH_NAMES[i].slice(0, 3),
    current: m.net,
    previous: previous?.months[i]?.net ?? 0,
  }));
  const hasPrevData = previous && (previous.totals.income !== 0 || previous.totals.expense !== 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-6" data-testid="yearly-report">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-heading text-lg font-semibold">Yıllık Görünüm</h2>
        <div className="flex items-center bg-secondary/50 border border-border rounded-full">
          <button data-testid="year-prev-button" onClick={() => setYear(year - 1)}
            className="p-2 hover:text-primary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="current-year-label" className="text-sm font-medium px-3">{year}</span>
          <button data-testid="year-next-button" onClick={() => setYear(year + 1)}
            className="p-2 hover:text-primary transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="yearly-income-card" className="bg-secondary/30 border border-border rounded-xl p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Yıllık Gelir</p>
          <p className="font-heading text-xl font-bold text-emerald-400">{formatTL(current.totals.income)}</p>
          <DeltaBadge current={current.totals.income} previous={previous?.totals.income} testId="yearly-income-delta" />
        </div>
        <div data-testid="yearly-expense-card" className="bg-secondary/30 border border-border rounded-xl p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Yıllık Gider</p>
          <p className="font-heading text-xl font-bold text-red-400">{formatTL(current.totals.expense)}</p>
          <DeltaBadge current={current.totals.expense} previous={previous?.totals.expense} invert testId="yearly-expense-delta" />
        </div>
        <div data-testid="yearly-net-card" className="bg-secondary/30 border border-primary/40 rounded-xl p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Yıllık Net</p>
          <p className={`font-heading text-xl font-bold ${current.totals.net >= 0 ? "text-primary" : "text-red-400"}`}>
            {formatTL(current.totals.net)}
          </p>
          <DeltaBadge current={current.totals.net} previous={previous?.totals.net} testId="yearly-net-delta" />
        </div>
      </div>

      <div className="h-64" data-testid="yearly-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} barGap={2}>
            <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              cursor={{ fill: "rgba(51,65,85,0.3)" }}
              contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#F8FAFC" }}
              formatter={(v, n) => [formatTL(v), n === "income" ? "Gelir" : n === "expense" ? "Gider" : "Net"]}
            />
            <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
            <Line dataKey="net" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2.5, fill: "#F59E0B" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div>
        <h3 className="font-heading text-base font-semibold mb-3">Yıl Karşılaştırması (Aylık Net)</h3>
        {hasPrevData ? (
          <div className="h-56" data-testid="year-comparison-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={compareData}>
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#F8FAFC" }}
                  formatter={(v, n) => [formatTL(v), n === "current" ? String(year) : String(year - 1)]}
                />
                <Legend formatter={(v) => (v === "current" ? String(year) : String(year - 1))} />
                <Line dataKey="current" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2.5, fill: "#F59E0B" }} />
                <Line dataKey="previous" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2.5, fill: "#94A3B8" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p data-testid="no-previous-year-message" className="text-sm text-muted-foreground py-6 text-center">
            {year - 1} yılına ait veri bulunmadığı için karşılaştırma gösterilemiyor.
          </p>
        )}
      </div>
    </div>
  );
}
